import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import i18n, { getAppText } from 'src/i18n';
import { showMessage } from '../../components/components/SnackbarNotification/snackbarNotificationSlice';

export interface ManagerTypeGroupSizeDto {
    [key: string]: any;
    id: number;
    referenceYear_Id: number;
    groupSize_Id: number;
    managerType_Id: number;
    bottomThreshold: number;
    topThreshold?: number;
}

export interface ManagerTypeGroupSizeState {
    managerTypeGroupSizes: ManagerTypeGroupSizeDto[];
    managerTypeGroupSize: ManagerTypeGroupSizeDto;
    gettingManagerTypeGroupSizes: boolean;
    gettingManagerTypeGroupSize: boolean;
    archivingManagerTypeGroupSize: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error?: string;
}

const blankManagerTypeGroupSizeDto: ManagerTypeGroupSizeDto = {
    id: 0,
    referenceYear_Id: 0,
    groupSize_Id: 0,
    managerType_Id: 0,
    bottomThreshold: 0,
    topThreshold: undefined,
};

const initialState: ManagerTypeGroupSizeState = {
    managerTypeGroupSizes: [],
    managerTypeGroupSize: blankManagerTypeGroupSizeDto,
    gettingManagerTypeGroupSizes: false,
    gettingManagerTypeGroupSize: false,
    archivingManagerTypeGroupSize: false,
    status: 'idle',
    error: undefined,
};

export const managerTypeGroupSizeSlice = createSlice({
    name: 'managerTypeGroupSizes',
    initialState,
    reducers: {
        clearManagerTypeGroupSizes: (state) => {
            state.managerTypeGroupSizes = [];
            state.managerTypeGroupSize = blankManagerTypeGroupSizeDto;
            state.status = 'loading';
        },
        updateManagerTypeGroupSize: (state, param) => {
            const { payload } = param;
            state.managerTypeGroupSize = payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getManagerTypeGroupSizes.pending, (state, action) => {
                state.gettingManagerTypeGroupSizes = true;
                state.status = 'loading';
            })
            .addCase(getManagerTypeGroupSizes.fulfilled, (state, action) => {
                state.gettingManagerTypeGroupSizes = false;
                state.managerTypeGroupSizes = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getManagerTypeGroupSizes.rejected, (state, action) => {
                state.gettingManagerTypeGroupSizes = false;
                state.managerTypeGroupSizes = [];
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(getManagerTypeGroupSize.pending, (state, action) => {
                state.gettingManagerTypeGroupSize = true;
                state.status = 'loading';
            })
            .addCase(getManagerTypeGroupSize.fulfilled, (state, action) => {
                state.gettingManagerTypeGroupSize = false;
                state.managerTypeGroupSize = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getManagerTypeGroupSize.rejected, (state, action) => {
                state.gettingManagerTypeGroupSize = false;
                state.managerTypeGroupSize = blankManagerTypeGroupSizeDto;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(archiveManagerTypeGroupSize.pending, (state, action) => {
                state.archivingManagerTypeGroupSize = true;
                state.status = 'loading';
            })
            .addCase(archiveManagerTypeGroupSize.fulfilled, (state, action) => {
                state.archivingManagerTypeGroupSize = false;
                state.managerTypeGroupSizes = state.managerTypeGroupSizes.filter((x) => x.id !== action.payload.id);
                state.status = 'succeeded';
            })
            .addCase(archiveManagerTypeGroupSize.rejected, (state, action) => {
                state.archivingManagerTypeGroupSize = false;
                state.status = 'failed';
                state.error = action.error.message;
            });
    },
});

export const { clearManagerTypeGroupSizes } = managerTypeGroupSizeSlice.actions;
export const { updateManagerTypeGroupSize } = managerTypeGroupSizeSlice.actions;

export const getManagerTypeGroupSizes = createAsyncThunk(
    'managerTypeGroupSizes/getManagerTypeGroupSizes',
    async (referenceYearId: number, thunkAPI) => {
        const response = await axios.get(`/api/synergy/manager-type-group-sizes/getConfigs/${referenceYearId}`);

        return response.data;
    }
);

export const getManagerTypeGroupSize = createAsyncThunk('managerTypeGroupSizes/getManagerTypeGroupSize', async (id: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/manager-type-group-sizes/${id}`);

    return response.data;
});

export const archiveManagerTypeGroupSize = createAsyncThunk(
    'managerTypeGroupSizes/archiveManagerTypeGroupSize',
    async (id: number, thunkAPI) => {
        const response = await axios.post(`/api/synergy/manager-type-group-sizes/archive/${id}`);
        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: getAppText('archivedManagerTypeGroupSize'),
            })
        );
        return response.data;
    }
);

export const saveManagerTypeGroupSize = createAsyncThunk(
    'managerTypeGroupSizes/saveManagerTypeGroupSize',
    async (managerTypeGroupSizeToSave: ManagerTypeGroupSizeDto, thunkAPI) => {
        if (managerTypeGroupSizeToSave.id === 0) {
            return axios
                .post(`/api/synergy/manager-type-group-sizes/create`, managerTypeGroupSizeToSave)
                .then((data) => {
                    thunkAPI.dispatch(
                        showMessage({
                            type: 'info',
                            message: getAppText('createManagerTypeGroupSize'),
                        })
                    );
                    return data;
                })
                .catch(async (error) => {
                    const message = await error.response.data.text();
                    thunkAPI.dispatch(
                        showMessage({
                            type: 'error',
                            message: i18n.t(message),
                        })
                    );
                    return error;
                });
        } else {
            return axios
                .put(`/api/synergy/manager-type-group-sizes/put`, managerTypeGroupSizeToSave)
                .then((data) => {
                    thunkAPI.dispatch(
                        showMessage({
                            type: 'info',
                            message: getAppText('saveManagerTypeGroupSize'),
                        })
                    );
                    return data;
                })
                .catch(async (error) => {
                    const message = await error.response.data.text();
                    thunkAPI.dispatch(
                        showMessage({
                            type: 'error',
                            message: i18n.t(message),
                        })
                    );
                    return error;
                });
        }
    }
);
